import { z } from 'zod'
import { scheduleEntrySchema } from '../helpers/sales-schedule.js'
import { MAX_BANK_DISCOUNT_MILLI } from '../helpers/sales-money.js'

/**
 * What a venue sets up before it can send a packet (docs/sales.spec.md,
 * "Everyone has it"): its contract templates, its saved payment schedules, its
 * Sales settings, and what's still missing.
 */

export const salesContractTemplateDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The template text (`lib/src/helpers/contract-template.ts`). */
  body: z.string(),
})

export const salesTemplatesDtoSchema = z.object({
  templates: z.array(salesContractTemplateDtoSchema),
  canEdit: z.boolean(),
})
export type SalesTemplatesDto = z.infer<typeof salesTemplatesDtoSchema>

export const salesScheduleDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  entries: z.array(scheduleEntrySchema),
})
export type SalesScheduleDto = z.infer<typeof salesScheduleDtoSchema>

export const salesSchedulesDtoSchema = z.object({
  schedules: z.array(salesScheduleDtoSchema),
  canEdit: z.boolean(),
})
export type SalesSchedulesDto = z.infer<typeof salesSchedulesDtoSchema>

export const salesSettingsShape = {
  /** A packet's "valid until", this many days after it's sent. */
  validDays: z.number().int().min(1).max(90),
  /** The couple is reminded this many days before a packet lapses; 0 is off. */
  lapseReminderDays: z.number().int().min(0).max(30),
  /** Off any payment made by bank transfer, in thousandths of a percent; 0 is none. */
  bankDiscountMilli: z.number().int().min(0).max(MAX_BANK_DISCOUNT_MILLI),
  /** Days before a payment is due to remind the couple; null is off. */
  reminderBeforeDays: z.number().int().min(1).max(60).nullable(),
  reminderOnDue: z.boolean(),
  /** Days after a payment is late to remind them, up to three times. */
  reminderAfterDays: z.array(z.number().int().min(1).max(60)).max(3),
}

/** What a venue's settings are until it changes them: the same as the database's defaults. */
export const DEFAULT_SALES_SETTINGS = {
  validDays: 14,
  lapseReminderDays: 3,
  bankDiscountMilli: 0,
  reminderBeforeDays: 7,
  reminderOnDue: true,
  reminderAfterDays: [1, 7],
} as const satisfies { [K in keyof typeof salesSettingsShape]: z.infer<(typeof salesSettingsShape)[K]> }

export const salesSettingsDtoSchema = z.object({ ...salesSettingsShape, canEdit: z.boolean() })
export type SalesSettingsDto = z.infer<typeof salesSettingsDtoSchema>

/** What a venue still needs before it can send a packet. */
export const salesReadinessDtoSchema = z.object({
  priceList: z.boolean(),
  contractTemplate: z.boolean(),
  schedule: z.boolean(),
  /** A business that can take payments is set up for the venue. */
  business: z.boolean(),
  /** Sending packets is open in this deployment. */
  sendingOpen: z.boolean(),
  /** What's missing, in plain sentences, in the order to do it. */
  missing: z.array(z.string()),
})
export type SalesReadinessDto = z.infer<typeof salesReadinessDtoSchema>
